import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import * as Yup from 'yup';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import { useOpenCaseMutation } from '@/api/member-management/admissionRequest';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { useLocation, useParams } from 'react-router-dom';
import {
	useGetAllEnumListQuery,
	useGetDiagnosisTreeListQuery,
	useGetDicListQuery,
	useGetStaffAllListQuery,
} from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import AppAutoCompleteMenu from '@/common/components/form-inputs/AppAutoCompleteMenu';

interface Props {
	open: boolean;
	onClose: () => void;
	details: any;
}

const NewOpenCaseDialog = (props: Props) => {
	const theme = useTheme();
	console.log(theme);
	const { open, onClose, details } = props;
	const { t } = useTranslation();
	const [openAssignCaseWorkers, setOpenAssignCaseWorkers] = useState(false);
	const [dateOfCaseOpen, setDateOfCaseOpen] = useState({});
	const [openCase] = useOpenCaseMutation();
	const { admissionRequestId } = useParams();
	const location = useLocation();
	const memberType = i18n.t(
		location.pathname.includes('admission-request-list')
			? 'member.admissionRequestLabel'
			: 'member.memberDetailsLabel'
	);

	const userInfo = useSelector(selectMyInfo);

	const { data: staffList }: any = useGetStaffAllListQuery(
		userInfo?.currentBranch?.id || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !userInfo?.currentBranch,
		}
	);

	const { data: diagnosisTreeList } = useGetDiagnosisTreeListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: allEnumList } = useGetAllEnumListQuery(null, {
		refetchOnMountOrArgChange: true,
	});

	const { data: suspectedMentalHealthProblemDicList } = useGetDicListQuery(
		'suspected_mental_health_problem',
		{ refetchOnMountOrArgChange: true }
	);

	const defaultValues: DefaultValues<any> = {};

	const formSchema = Yup.object().shape({
		dateOfCaseOpen: Yup.string()
			.required(t('errorMessage.required'))
			.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),

		diagnosisStatus: Yup.string().required(t('errorMessage.pleaseSelect')),

		mainDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis != '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),

		mainDiagnosisSpecify: Yup.string().when('mainDiagnosis', {
			is: (mainDiagnosis: string) => mainDiagnosis && mainDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),

		secondaryDiagnosis: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '1',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		secondaryDiagnosisSpecify: Yup.string().when('secondaryDiagnosis', {
			is: (secondaryDiagnosis: string) =>
				secondaryDiagnosis && secondaryDiagnosisIsOther(),
			then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
			otherwise: (schema) => schema.nullable(),
		}),

		suspectedMentalHealthProblem: Yup.string().when('diagnosisStatus', {
			is: (mainDiagnosis: string) => mainDiagnosis == '2',
			then: (schema) => schema.required(t('errorMessage.pleaseSelect')),
			otherwise: (schema) => schema.nullable(),
		}),
		suspectedMentalHealthProblemSpecify: Yup.string().when(
			'suspectedMentalHealthProblem',
			{
				is: (suspectedMentalHealthProblem: string) =>
					suspectedMentalHealthProblem && suspectedMentalHealthProblemIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}
		),
	});
	const formSchema2 = Yup.object().shape({
		responsibleCaseWorkerId: Yup.string().required(t('errorMessage.required')),
	});

	const methods = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const methods2 = useForm<any>({
		defaultValues,
		resolver: yupResolver(formSchema2),
	});

	useEffect(() => {
		if (details) {
			methods.setValue('diagnosisStatus', details.diagnosisStatus);
			methods.setValue('mainDiagnosis', details.mainDiagnosis);
			methods.setValue('mainDiagnosisSpecify', details.mainDiagnosisSpecify);
			methods.setValue('secondaryDiagnosis', details.secondaryDiagnosis);
			methods.setValue(
				'secondaryDiagnosisSpecify',
				details.secondaryDiagnosisSpecify
			);
			methods.setValue(
				'suspectedMentalHealthProblem',
				details.suspectedMentalHealthProblem
			);
			methods.setValue(
				'suspectedMentalHealthProblemSpecify',
				details.suspectedMentalHealthProblemSpecify
			);
		}
	}, [details]);

	const watchMainDiagnosis = methods.watch('mainDiagnosis');
	const watchDiagnosisStatus = methods.watch('diagnosisStatus');
	const watchSecondaryDiagnosis = methods.watch('secondaryDiagnosis');
	const watchSuspectedMentalHealthProblem = methods.watch(
		'suspectedMentalHealthProblem'
	);

	const handleClose = () => {
		methods.reset({});
		onClose();
	};

	const handleCloseAssignCaseWorkers = () => {
		methods2.reset({});
		onClose();
	};

	const onNext: SubmitHandler<any> = async (data) => {
		setOpenAssignCaseWorkers(true);
		setDateOfCaseOpen(data);
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		console.log(dateOfCaseOpen);
		const response = await openCase({
			...data,
			...dateOfCaseOpen,
			id: admissionRequestId,
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateMemberSuccess', { type: memberType }), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			handleCloseAssignCaseWorkers();
		}
	};

	const mainDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchMainDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};

	const secondaryDiagnosisIsOther = () => {
		const flatDiagnosisList = diagnosisTreeList?.data.flatMap((item: any) => [
			{ ...item },
			...(item.childrenList?.map((sub: any) => ({ ...sub })) || []),
		]);
		const obj = (flatDiagnosisList || []).find(
			(item: any) => item.id == watchSecondaryDiagnosis
		);
		if (obj && obj.labelEn === 'Others') return true;
		return false;
	};
	const suspectedMentalHealthProblemIsOther = () => {
		const obj = suspectedMentalHealthProblemDicList?.data.find(
			(item: any) => item.id == watchSuspectedMentalHealthProblem
		);
		if (obj && obj.code == '8') return true;
		return false;
	};

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
				<DialogTitle variant='h5'>{t('button.openCase')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.openCaseText')}
					</Typography>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onNext)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={6}>
									<AppDatePicker
										maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
										control={methods.control}
										name='dateOfCaseOpen'
										label={t('member.dateCaseOpen') + '*'}
									/>
								</Grid>
								<Grid size={12}>
									<Typography variant='body2' color='common.neutral40'>
										{t('member.diagnosisStatus') + '*'}
									</Typography>
									<AppRadioGroup
										control={methods.control}
										name={`diagnosisStatus`}
										row
										options={(allEnumList?.data.DiagnosisStatusEnum || []).map(
											(item: any) => {
												return {
													...item,
													id: item.code,
													name: item.desc,
												};
											}
										)}
										handleChange={() => {
											methods.setValue('mainDiagnosis', '');
											methods.setValue('mainDiagnosisSpecify', '');
											methods.setValue('secondaryDiagnosis', '');
											methods.setValue('secondaryDiagnosisSpecify', '');
											methods.setValue('suspectedMentalHealthProblem', '');
											methods.setValue(
												'suspectedMentalHealthProblemSpecify',
												''
											);
											methods.trigger('mainDiagnosisSpecify');
											methods.trigger('secondaryDiagnosisSpecify');
											methods.trigger('suspectedMentalHealthProblemSpecify');
										}}
									/>
								</Grid>
								{String(watchDiagnosisStatus) === '0' && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='mainDiagnosis'
												label={
													t('preAdmission.mainDiagnosis') +
													`${watchDiagnosisStatus != 2 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('mainDiagnosisSpecify', '');
													methods.clearErrors('mainDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!mainDiagnosisIsOther()}
												control={methods.control}
												name='mainDiagnosisSpecify'
												label={`${t('preAdmission.mainDiagnosisSpecify')}${
													mainDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}
								{watchDiagnosisStatus == 1 && (
									<>
										<Grid size={6}>
											<AppAutoCompleteMenu
												control={methods.control}
												name='mainDiagnosis'
												label={
													t('preAdmission.mainDiagnosis') +
													`${watchDiagnosisStatus != 2 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('mainDiagnosisSpecify', '');
													methods.clearErrors('mainDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!mainDiagnosisIsOther()}
												control={methods.control}
												name='mainDiagnosisSpecify'
												label={`${t('preAdmission.mainDiagnosisSpecify')}${
													mainDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
										<Grid size={6}>
											<AppAutoCompleteMenu
												optional={true}
												control={methods.control}
												name='secondaryDiagnosis'
												label={
													t('preAdmission.secondaryDiagnosis') +
													`${watchDiagnosisStatus == 1 ? '*' : ''}`
												}
												options={diagnosisTreeList?.data?.map((item: any) => {
													const childrenList = (item.childrenList || []).map(
														(child: any) => {
															return {
																...child,
																name: child.labelEn || '',
															};
														}
													);
													return {
														...item,
														childrenList: childrenList,
														name: item.labelEn || '',
													};
												})}
												handleChange={() => {
													methods.setValue('secondaryDiagnosisSpecify', '');
													methods.clearErrors('secondaryDiagnosisSpecify');
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!secondaryDiagnosisIsOther()}
												control={methods.control}
												name='secondaryDiagnosisSpecify'
												label={`${t('preAdmission.secondaryDiagnosisSpecify')}${
													secondaryDiagnosisIsOther() ? '*' : ''
												}`}
											/>
										</Grid>
									</>
								)}
								{watchDiagnosisStatus == 2 && (
									<>
										<Grid size={6}>
											<AppDropDownMenu
												optional={true}
												control={methods.control}
												name='suspectedMentalHealthProblem'
												label={
													t('preAdmission.suspectedMentalHealthProblem') +
													`${watchDiagnosisStatus == 2 ? '*' : ''}`
												}
												options={(
													suspectedMentalHealthProblemDicList?.data || []
												).map((item: { labelEn: any }) => {
													return {
														...item,
														name: item.labelEn,
													};
												})}
												handleChange={() => {
													methods.setValue(
														'suspectedMentalHealthProblemSpecify',
														''
													);
													methods.clearErrors(
														'suspectedMentalHealthProblemSpecify'
													);
												}}
											/>
										</Grid>
										<Grid size={6}>
											<AppTextField
												disabled={!suspectedMentalHealthProblemIsOther()}
												control={methods.control}
												name='suspectedMentalHealthProblemSpecify'
												label={`${t(
													'preAdmission.suspectedMentalHealthProblemSpecify'
												)}${suspectedMentalHealthProblemIsOther() ? '*' : ''}`}
											/>
										</Grid>
									</>
								)}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button sx={{ textTransform: 'none' }} onClick={() => handleClose()}>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onNext)}
					>
						{t('button.next')}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openAssignCaseWorkers}
				onClose={handleCloseAssignCaseWorkers}
				maxWidth='sm'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('member.assignCaseWorkers')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleCloseAssignCaseWorkers}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<Typography variant='body2' sx={{ marginBottom: '12px' }}>
						{t('member.openCaseText2')}
					</Typography>
					<FormProvider {...methods2}>
						<form onSubmit={methods2.handleSubmit(onSubmit)}>
							<Grid
								container
								spacing={2}
								marginTop={0}
								marginBottom={2}
								alignItems='center'
							>
								<Grid size={12}>
									<AppDropDownMenu
										control={methods2.control}
										name='responsibleCaseWorkerId'
										label={t('member.responsibleCaseWorker') + '*'}
										options={staffList?.data || []}
									/>
								</Grid>
								<Grid size={12}>
									<AppDropDownMenu
										optional={true}
										control={methods2.control}
										name='relievingWorkerId'
										label={t('member.relievingWorker')}
										options={staffList?.data || []}
									/>
								</Grid>
								<Grid size={12}>
									<AppMultipleDropDownMenu
										control={methods2.control}
										name='sharedWithProfessionalList'
										label={t('member.sharedWithProfessionals')}
										options={staffList?.data || []}
									/>
								</Grid>
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ textTransform: 'none' }}
						onClick={() => handleCloseAssignCaseWorkers()}
					>
						{t('button.cancel')}
					</Button>
					<Button
						type='submit'
						variant='contained'
						onClick={methods2.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewOpenCaseDialog;
